import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const KeycloakContext = createContext();

export const useKeycloak = () => {
  const context = useContext(KeycloakContext);
  if (!context) {
    throw new Error("Keycloak error");
  }
  return context;
};

const TOKEN_REFRESH_MIN_VALIDITY = 70;
const TOKEN_CHECK_INTERVAL_MS = 60000;

function buildUserInfo(kc) {
  if (!kc || !kc.tokenParsed) return {};
  return {
    username: kc.tokenParsed.preferred_username,
    email: kc.tokenParsed.email,
    fullName: kc.tokenParsed.name,
    token: kc.token,
    employeeid: kc.tokenParsed.employeeId || kc.tokenParsed.employee_id,
  };
}

export const KeycloakProvider = ({ children }) => {
  const [keycloak, setKeycloak] = useState(null);
  const [userAuthenticated, setUserAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!window.Keycloak) {
      console.error("Keycloak not available");
      setLoading(false);
      return;
    }

    const keycloakConfig = {
      url: process.env.REACT_APP_KEYCLOAK_URL,
      realm: process.env.REACT_APP_KEYCLOAK_REALM,
      clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID,
    };

    const keycloakInstance = new window.Keycloak(keycloakConfig);

    keycloakInstance
      .init({
        onLoad: "login-required",
        checkLoginIframe: false,
      })
      .then((authenticated) => {
        if (authenticated) {
          setKeycloak(keycloakInstance);
          setUserAuthenticated(true);
          setUserInfo(buildUserInfo(keycloakInstance));
        } else {
          console.log("User not authenticated");
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("keycloak error:", error);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!keycloak || !userAuthenticated) return;

    const refreshIfNeeded = () => {
      keycloak
        .updateToken(TOKEN_REFRESH_MIN_VALIDITY)
        .then((refreshed) => {
          if (refreshed) {
            setUserInfo(buildUserInfo(keycloak));
          }
        })
        .catch(() => {
          keycloak.login();
        });
    };

    const intervalId = setInterval(refreshIfNeeded, TOKEN_CHECK_INTERVAL_MS);
    refreshIfNeeded();

    return () => clearInterval(intervalId);
  }, [keycloak, userAuthenticated]);

  const getToken = useCallback(() => keycloak?.token, [keycloak]);
  const refreshToken = useCallback(
    () => (keycloak ? keycloak.updateToken(30) : Promise.resolve(false)),
    [keycloak]
  );

  const value = useMemo(
    () => ({
      keycloak,
      userAuthenticated,
      userInfo,
      loading,
      getToken,
      refreshToken,
    }),
    [keycloak, userAuthenticated, userInfo, loading, getToken, refreshToken]
  );

  return (
    <KeycloakContext.Provider value={value}>
      {children}
    </KeycloakContext.Provider>
  );
};
